import { SystemStatusAppliance } from '@ekkogmbh/apisdk';
import Typography from '@mui/material/Typography';

interface Props {
  appliance: SystemStatusAppliance;
}

/**
 * Extended technology type for appliance title display.
 *
 * The backend sends additional technology-specific fields (location, company, store, group, etc.)
 * that are not currently typed in the @ekkogmbh/apisdk SystemStatusAppliance interface.
 * The SDK only types `name` and `baseUrl` for the technology object.
 *
 * We define this local interface to access these fields with type safety rather than updating
 * the SDK, as the SystemStatus API is planned to be deprecated in favor of the new
 * DeviceIntegration API (which is not yet complete). Refactoring the SDK types for a
 * deprecated API is not worthwhile at this point.
 */
interface ApplianceTechnology {
  name: string;
  location?: string; // pdi-digital
  company?: string; // solumcloud
  store?: string; // solumcloud
}

const getTitleSuffix = (technology: ApplianceTechnology): string => {
  if (technology.location) {
    return ` (${technology.location})`;
  }
  if (technology.company && technology.store) {
    return ` (${technology.company}/${technology.store})`;
  }
  return '';
};

export const ApplianceTitle = ({ appliance }: Props): React.JSX.Element => {
  const { name } = appliance.technology;
  const { factoryId } = appliance.tags;
  const suffix = getTitleSuffix(appliance.technology as ApplianceTechnology);

  return (
    <Typography color="textPrimary">
      {name} {factoryId}
      {suffix}
    </Typography>
  );
};
